import {IcnTriangleWarning} from "../../assets/icons";
import {ReactComponent as LogoBrandmark} from "../../assets/images/logo-new-brandmark.svg";

import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from "react-router-dom";
import * as Yup from "yup";
import {Formik, Form, FormikHelpers} from "formik";

import {useAuth} from "../../providers/AuthProvider";

import FormFieldset from "../form-elements/FormFieldset";
import FormInputPasswordShowHide from "../form-elements/FormInputPasswordShowHide";
import FormGroupErrorText from "../form-elements/FormGroupErrorText";
import FormSubmitValidation from "../form-elements/FormSubmitValidation";
import Button from "../common/Button";
import ButtonLink from "../common/ButtonLink";
import Alert from "../common/Alert";

interface FormValues {
  password1: string;
  password2: string;
}

const ResetPasswordChangeFormSchema = Yup.object().shape({
  password1: Yup.string().required("Please enter new password"),
  password2: Yup.string()
    .required("Please confirm new password")
    .oneOf([Yup.ref('password1')], "Both password fields must match")
});

function ResetPasswordChangeForm() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { resetPassword, validateResetToken } = useAuth();

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        navigate('/reset-password', {
          state: { error: 'Reset token is missing. Please request a new password reset.' }
        });
        return;
      }

      setIsValidating(true);
      const isValid = await validateResetToken(token);

      if (!isValid) {
        navigate('/reset-password', {
          state: { error: 'Invalid or expired reset link. Please request a new password reset.' }
        });
      } else {
        setTokenValid(true);
        setIsValidating(false);
      }
    };

    checkToken();
  }, [token, validateResetToken, navigate]);
  const handleSubmit = async (
    values: FormValues,
    { setSubmitting, setStatus }: FormikHelpers<FormValues>
  ) => {
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      if (!token) {
        throw new Error('Reset token is missing');
      }

      await resetPassword(token, values.password1, values.password2);
      setSuccess(true);
      setStatus('submitted');
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Token validation errors (invalid or expired)
        setError("Invalid or expired reset link. Please request a new password reset.");
      } else if (err.response?.data?.password) {
        // Django password validation errors
        const passwordErrors = err.response.data.password;
        setError(Array.isArray(passwordErrors) ? passwordErrors[0] : passwordErrors);
      } else if (err.response?.data?.detail) {
        // DRF standard error format
        setError(err.response.data.detail);
      } else if (err.response) {
        setError("An error occurred while resetting your password. Please try again later.");
      } else {
        setError(err.message || "An error occurred during password reset.");
      }
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <form className="c-form" noValidate>
        <i className="c-public-page-section-content-logo">
          <LogoBrandmark/>
        </i>
        <div className="c-public-page-section-content-header">
          <h1 className="h5">Reset password</h1>
          <p>Validating your reset link...</p>
        </div>
      </form>
    );
  }

  if (!tokenValid) {
    return null;
  }

  return (
    <Formik
      initialValues={{
        password1: "",
        password2: ""
      }}
      validateOnMount={true}
      validateOnChange={true}
      validationSchema={ResetPasswordChangeFormSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, status, errors, touched, dirty, isValid }) => {

        return (
          <>
            {status === "submitted" && success ? (
              <FormSubmitValidation type="success">
                <h1 className="h5">
                  Your password was successfully reset!
                </h1>

                <p>
                  Your new password has been set. You may go ahead and log in now.
                </p>

                <ButtonLink url="/login" btnSize='lg' text="Continue to Login"/>

              </FormSubmitValidation>
            ):(

              <Form
                className="c-form"
                noValidate
              >
                <i className="c-public-page-section-content-logo">
                  <LogoBrandmark/>
                </i>

                <div className="c-public-page-section-content-header">
                  <h1 className="h5">
                    Reset password
                  </h1>

                  <p>Please enter your new password twice so we can verify you typed it in correctly.</p>
                </div>

                <FormFieldset
                  extraClassName="authentication"
                  legendText='Enter new password'
                >

                  <FormInputPasswordShowHide
                    extraClassName="boxed"
                    formGroupStyle="block-line"
                    labelText="Password"
                    name="password1"
                    placeholder="New password"
                    hideError
                  />

                  <FormInputPasswordShowHide
                    extraClassName="boxed"
                    formGroupStyle="block-line"
                    labelText="Password"
                    name="password2"
                    placeholder="Confirm password"
                    hideError
                  />

                  {(errors.password1 && touched.password1 && errors.password2 && touched.password2)
                    ? (<FormGroupErrorText text="Please enter and confirm new password to proceed"/>)
                    : errors.password2 && touched.password2 ?
                      (<FormGroupErrorText text={errors.password2}/>):
                      null
                  }

                </FormFieldset>

                <Button
                  type="submit"
                  btnSize="lg"
                  disabled={!isValid || isSubmitting || !dirty}
                  isLoading={isLoading}
                  isLoadingText="Resetting password"
                  text="Change my password"
                />

                {error && (
                  <Alert
                    type="danger"
                    alertRadius="rounded12px"
                    alertStyle="bordered"
                    icon={<IcnTriangleWarning/>}
                    text={error}
                  />
                )}

              </Form>
            )}
          </>
        )
      }}
    </Formik>
  );
}

export default ResetPasswordChangeForm;
